import { useRouter } from 'next/router'
import { useState, type FC } from 'react'
import clsx from 'clsx'

import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { Link } from '@wartek-id/link'

import usePenetapanDinas from 'app/KinerjaDinasPage/hooks/usePenetapanDinas'
import { Header } from 'app/KinerjaShared/components/Header'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import { PENGELOLAAN_KINERJA_KADIN_PATH } from 'app/KinerjaShared/constants/path'
import BadgeStatusPengiriman from 'app/KinerjaShared/BadgeStatusPengiriman'
import CardSummaryPenetapan from 'app/KinerjaDinasPage/components/CardSummaryPenetapan'
import { FilterSearchStatusPengiriman } from 'app/KinerjaShared/components/FilterSearchStatusPengiriman'
import { type TPenilaian } from 'types/kinerja-dinas'
import { PANDUAN_PENGELOLAAN_DINAS_URL } from 'app/KinerjaShared/utils/panduan'
import DialogPredikatKinerjaUpdate from 'app/KinerjaDinasPage/components/DialogPredikatKinerjaUpdate'
import useTabKsGuru from 'app/KinerjaShared/hooks/useTabKsGuru'
import DialogNoLongerDirectReport from 'app/KinerjaDinasPage/components/DialogNoLongerDirectReport'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import DialogUnconfirmedDirectReport from 'app/KinerjaDinasPage/components/DialogUnconfirmedDirectReport'

import type { FilterItem } from 'app/KinerjaShared/components/FilterSearchStatusPengiriman'

interface DinasPenetapanKirimProps {}

const RenderCell: FC<{ item: TPenilaian; value: React.ReactElement }> = ({
  item,
  value,
}) => {
  // If the observation status is not 'PREPARATION', return the value.
  // And if the value is not available, return null.
  if (!!item?.predikatKinerja) return value || null

  // Otherwise, return ''
  return <></>
}

const DinasPenetapanKirim: FC<DinasPenetapanKirimProps> = ({}) => {
  const router = useRouter()
  const [dialogOpen, setDialogOpen] = useState<
    'update-predikat-kinerja' | null
  >(null)
  const [selectedItem, setSelectedItem] = useState<TPenilaian | null>(null)

  const [activeParams, setActiveParams] = useState<[FilterItem[], string]>([
    ['ALL'],
    '',
  ])

  // To store the item that has been confirmed as 'REJECTED' by the subordinate
  const [obsoleteItem, setObsoleteItem] = useState<TPenilaian>(null)

  // Tabs
  const { render: tabs, activeTabName } = useTabKsGuru()

  const {
    // Kepala Sekolah
    metasKs,
    refetchListKs,

    // Guru
    metasGuru,
    refetchListGuru,

    // Combined
    isListLoading,
    hasItems,

    // Relative to active tab
    relativeItems,
    isRelativeError,
    isRelativeSuccess,
  } = usePenetapanDinas({
    activeTabName,
    name: activeParams?.[1] || undefined,
    status: activeParams?.[0] || undefined,
  })

  const { profilePs } = useRoleContext()

  return (
    <div>
      <Header
        title="Penetapan Predikat Kinerja Pegawai"
        className="mb-9 h-14 print:!hidden"
        onBack={() => {
          router.back()
        }}
        rightNode={
          <div className="bg-white rounded">
            <Button
              as="a"
              variant="ghost"
              rel="noopener noreferrer"
              className="flex gap-1.5 h-14"
              size="md"
              href={PANDUAN_PENGELOLAAN_DINAS_URL}
              target="_blank"
            >
              <Icon fontSize="sm" color="informational">
                help
              </Icon>
              Baca panduan
            </Button>
          </div>
        }
      />

      <section className="bg-white border border-subdued rounded-lg mt-6">
        <div className="px-6 pt-6">
          {/* Title */}
          <div className="space-y-1">
            <Text variant="heading-md">
              Daftar Penetapan Predikat Kinerja Pegawai
            </Text>
            <Text color="subdued">
              Data mungkin muncul secara berkala. Anda dapat ubah rekomendasi
              jika perlu.
            </Text>
          </div>

          {/* Summary */}
          <CardSummaryPenetapan
            onClickSend={() => {
              router.push(
                `${PENGELOLAAN_KINERJA_KADIN_PATH}/penetapan/kirim/konfirmasi`
              )
            }}
          />
        </div>

        {/* Tabs */}
        {tabs}

        {/* Filter & Search */}
        <FilterSearchStatusPengiriman
          className="px-5 my-5"
          isLoading={isListLoading}
          data={activeTabName === 'GURU' ? metasGuru?.counts : metasKs?.counts}
          onChange={({ filter, search }) => setActiveParams([filter, search])}
        />

        {/* Table */}
        <TablePelaksanaanKinerja className="mt-0">
          <TableHeader>
            <TableHeaderItem className="w-2/12">
              <Text variant="body" color="default" className="!font-bold">
                Nama
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Jabatan
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Satuan Pendidikan
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Rekomendasi
                <br />
                Predikat Kinerja
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Status Pengiriman
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0" />
          </TableHeader>
          <TableBody>
            {/* If loading */}
            {isListLoading && <TableShimmer columns={6} />}

            {/* If error or no data returned */}
            {!isListLoading &&
              (isRelativeError || relativeItems?.length === 0) && (
                <TableRowStates
                  colSpan={6}
                  type={isRelativeError ? 'ERROR' : 'EMPTY'}
                  onClick={() => {
                    activeTabName === 'GURU'
                      ? refetchListGuru()
                      : refetchListKs()
                  }}
                >
                  <>
                    Pastikan terdapat pegawai dengan jabatan tersebut di daerah
                    <br />
                    Anda dan sudah menyelesaikan tahap Pelaksanaan Kinerja.
                  </>
                </TableRowStates>
              )}

            {!isListLoading &&
              isRelativeSuccess &&
              hasItems &&
              relativeItems.map((item, idx) => {
                const isSubordinate = item?.confirmedSubordinate === 'CONFIRMED'
                const isSent =
                  item?.statePengiriman?.toLowerCase() === 'sudah_dikirim'
                return (
                  <TableRow key={`ks_${item.npsn}_${idx}`}>
                    <TableRowItem className="align-middle">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.name || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.jabatan || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.satuanPendidikan || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      {/* If kepala sekolah is reported as subordinate */}
                      {isSubordinate ? (
                        <>
                          {!!item.predikatKinerja ? (
                            <Text
                              variant="subheading"
                              color="informational"
                              className="overflow-wrap-anywhere uppercase !text-blue-60"
                            >
                              {item?.predikatKinerja}
                            </Text>
                          ) : (
                            <Text variant="body-sm" color="disabled">
                              Belum selesai dinilai
                            </Text>
                          )}
                        </>
                      ) : (
                        <>
                          {/* If kepala sekolah confirmed that the user is not their superior */}
                          {item.confirmedSubordinate === 'REJECTED' && (
                            <Text variant="body-sm" color="disabled">
                              Akses dihapus
                            </Text>
                          )}

                          {/* If kepala sekolah haven't confirm */}
                          {item.confirmedSubordinate === 'UNCONFIRMED' && (
                            <Text variant="body-sm" color="critical">
                              Kepala Sekolah belum konfirmasi data atasan
                            </Text>
                          )}
                        </>
                      )}
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      {/* If kepala sekolah is reported as subordinate */}
                      {isSubordinate ? (
                        <RenderCell
                          item={item}
                          value={
                            <BadgeStatusPengiriman
                              status={item.statePengiriman}
                            />
                          }
                        />
                      ) : (
                        <Link
                          as="button"
                          onClick={() => setObsoleteItem(item)}
                          tabIndex={0}
                          underline
                          variant="primary"
                        >
                          Info
                        </Link>
                      )}
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <div className="flex flex-row items-center space-x-6">
                        {/* If kepala sekolah is reported as subordinate */}
                        {isSubordinate && (
                          <RenderCell
                            item={item}
                            value={
                              <Button
                                size="sm"
                                color="white"
                                disabled={
                                  isSent || // Disable if the item has been sent
                                  item.confirmedSubordinate === 'REJECTED' ||
                                  item.confirmedSubordinate === 'UNCONFIRMED'
                                }
                                className={clsx({ '!outline-0': isSent })}
                                onClick={() => {
                                  setDialogOpen('update-predikat-kinerja')
                                  setSelectedItem(item)
                                }}
                              >
                                Ubah Predikat Kinerja
                              </Button>
                            }
                          />
                        )}
                      </div>
                    </TableRowItem>
                  </TableRow>
                )
              })}
          </TableBody>
        </TablePelaksanaanKinerja>
      </section>

      <DialogPredikatKinerjaUpdate
        activeTabName={activeTabName}
        data={selectedItem}
        isOpen={dialogOpen === 'update-predikat-kinerja'}
        onClose={() => {
          setSelectedItem(null)
          setDialogOpen(null)
        }}
      />

      {/* Dialog: ... sudah konfirmasi bahwa Anda tidak lagi menjadi atasannya */}
      <DialogNoLongerDirectReport
        isOpen={
          !!obsoleteItem && obsoleteItem.confirmedSubordinate === 'REJECTED'
        }
        name={obsoleteItem?.name}
        regionName={profilePs?.namaWilayah}
        onClose={() => setObsoleteItem(null)}
        onClick={() => setObsoleteItem(null)}
      />

      {/* Dialog: Kabari Kepala Sekolah untuk konfirmasi data atasan */}
      <DialogUnconfirmedDirectReport
        isOpen={
          !!obsoleteItem && obsoleteItem.confirmedSubordinate === 'UNCONFIRMED'
        }
        onClose={() => setObsoleteItem(null)}
        onClick={() => setObsoleteItem(null)}
      />
    </div>
  )
}
export default DinasPenetapanKirim
